import { useState, useRef, useEffect } from 'react'
import { useWidgetContext } from '../context/WidgetContext'
import { captureClientMeta, capturePageContext } from '../helpers/pageContext'

type ChatMessage = {
  content: string
  isUser: boolean
  timestamp: number
  metadata?: any
}

const normalizeAssistantMetadata = (message: any): any => {
  const baseMetadata =
    message?.metadata && typeof message.metadata === 'object' ? { ...message.metadata } : {}

  const ragSources = Array.isArray(message?.ragSources)
    ? message.ragSources
    : Array.isArray(message?.rag_sources)
      ? message.rag_sources
      : Array.isArray(baseMetadata?.ragSources)
        ? baseMetadata.ragSources
        : Array.isArray(baseMetadata?.rag_sources)
          ? baseMetadata.rag_sources
          : []

  const kbSources = Array.isArray(message?.kbSources)
    ? message.kbSources
    : Array.isArray(message?.kb_sources)
      ? message.kb_sources
      : Array.isArray(baseMetadata?.kbSources)
        ? baseMetadata.kbSources
        : Array.isArray(baseMetadata?.kb_sources)
          ? baseMetadata.kb_sources
          : []

  const siteCrawlSources = Array.isArray(message?.siteCrawlSources)
    ? message.siteCrawlSources
    : Array.isArray(message?.site_crawl_sources)
      ? message.site_crawl_sources
      : Array.isArray(baseMetadata?.siteCrawlSources)
        ? baseMetadata.siteCrawlSources
        : Array.isArray(baseMetadata?.site_crawl_sources)
          ? baseMetadata.site_crawl_sources
          : []

  return {
    ...baseMetadata,
    ...(ragSources.length > 0 ? { ragSources, rag_sources: ragSources } : {}),
    ...(kbSources.length > 0 ? { kbSources, kb_sources: kbSources } : {}),
    ...(siteCrawlSources.length > 0
      ? { siteCrawlSources, site_crawl_sources: siteCrawlSources }
      : {}),
  }
}

export type ChatAgentHook = {
  messages: ChatMessage[]
  hasError: boolean
  isChatLoading: boolean
  sendMessage: (prompt: string, onBeforeCallAgent?: () => void) => Promise<void>
  resetChat: () => void
  stopMessage: () => void

  isThinking: boolean
  thinkingTime: number
}

export const useChatAgent = (): ChatAgentHook => {
  const { agent, chatApi, mode, config } = useWidgetContext()

  const bridge = config.mobileMcpToolBridge
  const activeRequest = useRef<AbortController | null>(null)
  const [bridgeLoading, setBridgeLoading] = useState(false)
  useEffect(() => () => activeRequest.current?.abort(), [])
  const stopMessage = () => {
    activeRequest.current?.abort()
    activeRequest.current = null
    setBridgeLoading(false)
  }

  const defaultGreeting =
    agent.greeting?.trim() ||
    (mode === 'search'
      ? 'What would you like to search for today?'
      : mode === 'code'
        ? 'Describe the code or HTML change you need.'
      : mode === 'image'
        ? 'Describe the image you want to create.'
        : mode === 'video'
          ? 'Describe the video you want to generate.'
          : mode === 'speech'
            ? 'Speech mode is ready. You can speak or type your message.'
            : 'Hello, how may I help you today?')

  const [hasError, setHasError] = useState(false)
  const [chatId, setChatId] = useState<string | null>(null)

  // 🔹 Use agent greeting only for INITIAL message
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      content: defaultGreeting,
      isUser: false,
      timestamp: Date.now(),
      metadata: undefined,
    },
  ])

  const { useInvokeChatMutation } = chatApi
  const [invokeChat, { isLoading }] = useInvokeChatMutation()

  const generateConversationList = (
    content: string,
    isUser: boolean,
    metadata?: any
  ) => {
    setMessages((prev) => [
      ...prev,
      { content, isUser, timestamp: Date.now(), metadata },
    ])
  }

  const sendMessage = async (prompt: string, onBeforeCallAgent?: () => void) => {
    if (activeRequest.current || isLoading || !prompt.trim()) return
    setHasError(false)

    generateConversationList(prompt, true)
    onBeforeCallAgent?.()

    try {
      if (bridge) {
        const controller = new AbortController()
        activeRequest.current = controller
        setBridgeLoading(true)
        let assistantIndex: number | undefined
        const updateAssistant = (content: string, metadata?: any) => {
          if (controller.signal.aborted || activeRequest.current !== controller) return
          setMessages(prev => {
            const next = [...prev]
            if (assistantIndex === undefined) assistantIndex = next.length
            next[assistantIndex] = { content, isUser: false, timestamp: Date.now(), metadata }
            return next
          })
        }
        try {
          const result = await bridge.send({
            prompt, agentId: agent.id, chatId: chatId ?? undefined,
            signal: controller.signal, onText: text => updateAssistant(text),
          })
          if (!controller.signal.aborted && activeRequest.current === controller) {
            if (result.chatId) setChatId(result.chatId)
            updateAssistant(result.text, result.metadata)
          }
        } catch (error) {
          if (!controller.signal.aborted) throw error
        } finally {
          if (activeRequest.current === controller) {
            activeRequest.current = null
            setBridgeLoading(false)
          }
        }
        return
      }
      if (!agent.id) return

      // Captured at send-time (not mount-time) so it reflects the current
      // page/route even if the visitor navigated within a single-page app
      // after the widget first mounted.
      const pageContext = capturePageContext()
      const clientMeta = captureClientMeta()

      const chatResponse = await invokeChat({
        prompt,
        agentId: agent.id,
        chatId: chatId ?? undefined,
        pageContext,
        clientMeta,
      }).unwrap()

      if (!chatId) {
        setChatId(chatResponse.chat.id)
      }

      const assistantMessage = chatResponse?.message ?? {}
      const assistantText = assistantMessage?.content ?? ''
      const assistantMetadata = normalizeAssistantMetadata(assistantMessage)

      generateConversationList(
        assistantText,
        false,
        assistantMetadata
      )
    } catch (error) {
      console.error('Chat error:', error)
      setHasError(true)
    }
  }

  // ⛔ unchanged per your request
  const resetChat = () => {
    stopMessage()
    bridge?.reset?.()
    setChatId(null)
    setMessages([
      {
        content: defaultGreeting,
        isUser: false,
        timestamp: Date.now(),
        metadata: undefined,
      },
    ])
    setHasError(false)
  }

  return {
    messages,
    sendMessage,
    hasError,
    isChatLoading: isLoading || bridgeLoading,
    stopMessage,
    resetChat,
    isThinking: false,
    thinkingTime: 0,
  }
}
